import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  NotebookPen,
  Cog,
  ClipboardCheck,
  ClipboardList,
  Package,
  Boxes,
  Warehouse,
  Activity,
  FileBarChart,
  CalendarDays,
  Images,
  ShieldCheck,
  LogOut,
  Settings,
  Menu,
  ChevronDown,
} from "lucide-react";
import SectionSwitcher from "@/components/SectionSwitcher";
import { useAuth, usePerms } from "@/context/AuthContext";
import { useTbm } from "@/context/TbmContext";
import { useLang } from "@/context/LangContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { preloadRoute } from "@/lib/routePreload";
import { prefetchRouteData } from "@/lib/routeData";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/daily-logs", key: "daily_logs", icon: NotebookPen },
  { to: "/equipment", key: "equipment", icon: Cog },
  { to: "/checklists", key: "checklists", icon: ClipboardCheck },
  { to: "/checklist-records", key: "checklist_records", icon: ClipboardList },
  { to: "/parts", key: "parts", icon: Package },
  { to: "/consumption", key: "consumption", icon: Boxes },
  { to: "/stock", key: "stock", icon: Warehouse },
  { to: "/health", key: "health", icon: Activity },
  { to: "/daily-reports", key: "daily_reports", icon: FileBarChart },
  { to: "/calendar", key: "calendar", icon: CalendarDays },
  { to: "/job-site-pictures", key: "job_site_pictures", icon: Images },
  { to: "/master", key: "master", icon: ShieldCheck, master: true },
];

function TbmSwitcher() {
  const { tbms, selected, selectTbm } = useTbm();
  const { t } = useLang();
  if (!tbms.length) return <span className="text-sm text-ink3">{t("no_machine")}</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="tbm-switcher"
          className="flex items-center gap-2 rounded-lg border border-hairline bg-surf-2 px-3 py-2 text-sm font-semibold text-ink transition-colors hover:border-brand"
        >
          <span className="h-2 w-2 rounded-full bg-brand" />
          <span className="max-w-[180px] truncate">{selected?.name || "Select machine"}</span>
          <ChevronDown size={16} className="text-ink3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        {tbms.map((tbm) => (
          <DropdownMenuItem
            key={tbm.id}
            data-testid={`tbm-option-${tbm.id}`}
            onClick={() => selectTbm(tbm.id)}
            className="flex flex-col items-start"
          >
            <span className="font-semibold">{tbm.name}</span>
            {tbm.model ? <span className="text-xs text-ink3">{tbm.model}</span> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarContent({ onNavigate }) {
  const { t } = useLang();
  const { user } = useAuth();
  const { selected } = useTbm();
  const isMaster = !!user?.is_master;
  const nav = NAV.filter((item) => !item.master || isMaster);
  const warm = (to) => { preloadRoute(to); prefetchRouteData(to, selected?.id); };
  return (
    <div className="flex h-full flex-col">
      <SectionSwitcher section="tbm" onNavigate={onNavigate} />
      <nav className="flex-1 space-y-1 px-3">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.key}`}
              onClick={onNavigate}
              onMouseEnter={() => warm(item.to)}
              onFocus={() => warm(item.to)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-brand/15 text-brand" : "text-ink2 hover:bg-surf-3 hover:text-ink"
                }`
              }
            >
              <Icon size={19} />
              {t(item.key)}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 text-[10px] uppercase tracking-widest text-ink3">BoreHub Web Console</div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isAdmin } = usePerms();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-surf text-ink">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-hairline bg-surf-2 lg:block">
        <div className="sticky top-0 h-screen">
          <SidebarContent />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-hairline bg-surf/95 px-4 py-3 backdrop-blur lg:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="sidebar-toggle" className="rounded-lg p-2 text-ink2 hover:bg-surf-3 lg:hidden">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-hairline bg-surf-2 p-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <TbmSwitcher />

          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <button
                data-testid="settings-button"
                onClick={() => navigate("/settings")}
                className="rounded-lg p-2 text-ink2 hover:bg-surf-3"
                title={t("settings")}
              >
                <Settings size={20} />
              </button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="flex items-center gap-2 rounded-full border border-hairline bg-surf-2 py-1 pl-1 pr-3"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-extrabold text-black">
                    {(user?.name || "U").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden text-sm font-semibold sm:block">{user?.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-semibold text-ink">{user?.name}</div>
                  <div className="text-xs text-ink3">{user?.role}</div>
                </div>
                <DropdownMenuItem data-testid="logout-button" onClick={doLogout} className="text-danger">
                  <LogOut size={16} className="mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
